import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Star, Search, Image, MessageSquare, Clock } from 'lucide-react';
import { PageSEO } from '@/components/SEO';

const fordeler = [
  {
    icon: MapPin,
    title: 'Lokal synlighet',
    description: 'Bli synlig når kunder søker etter tjenester i ditt nærområde.',
  },
  {
    icon: Star,
    title: 'Anmeldelser',
    description: 'Bygg tillit med positive kundeanmeldelser som vises i søk.',
  },
  {
    icon: Search,
    title: 'Google Maps',
    description: 'Vis bedriften din på kartet og gjør det enkelt å finne deg.',
  },
  {
    icon: Image,
    title: 'Bilder og innlegg',
    description: 'Del bilder og oppdateringer direkte i søkeresultatene.',
  },
  {
    icon: MessageSquare,
    title: 'Direktemeldinger',
    description: 'La kunder kontakte deg direkte fra Google-søk.',
  },
  {
    icon: Clock,
    title: 'Åpningstider',
    description: 'Vis alltid oppdaterte åpningstider til kundene dine.',
  },
];

const tjenester = [
  { title: 'Oppretting av profil', description: 'Vi setter opp en komplett Google Bedriftsprofil for deg.' },
  { title: 'Optimalisering', description: 'Vi optimaliserer profilen for best mulig synlighet.' },
  { title: 'Innholdsstrategi', description: 'Jevnlige innlegg og oppdateringer som engasjerer.' },
  { title: 'Anmeldelseshåndtering', description: 'Hjelp med å få flere og bedre anmeldelser.' },
];

export default function GoogleBedriftsprofil() {
  return (
    <div>
      <PageSEO title="Google Bedriftsprofil – Lokal SEO for hele Norge" path="/tjenester/google-bedriftsprofil" description="Bli synlig lokalt med Google Bedriftsprofil. Vi hjelper bedrifter i hele Norge med å bli funnet på Google Maps og øke trafikken." />
      {/* Hero */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="gbp-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                Tjeneste
              </p>
              <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
                Google Bedriftsprofil
              </h1>
              <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
                Bli den første til å dukke opp når kunder søker etter det du tilbyr. Med en optimalisert Google Bedriftsprofil er du tilgjengelig i rett øyeblikk, til rett tid.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group mt-8"
                data-testid="gbp-cta"
              >
                Optimaliser din profil
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            <div className="relative group rounded-3xl overflow-hidden shadow-2xl ring-1 ring-primary/5">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-accent/20 rounded-3xl" />
              <img
                src="https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?auto=format&fit=crop&q=80"
                alt="Google Bedriftsprofil og lokal markedsføring – Side Kraft"
                className="relative z-10 w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Fordeler */}
      <section className="py-24 md:py-32" data-testid="gbp-fordeler">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Fordeler
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight">
              Hvorfor Google Bedriftsprofil?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {fordeler.map((fordel, index) => (
              <div
                key={index}
                className="p-8 border border-border rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`gbp-fordel-${index}`}
              >
                <fordel.icon className="w-10 h-10 text-accent mb-6" />
                <h3 className="font-syne font-bold text-xl text-primary mb-3">
                  {fordel.title}
                </h3>
                <p className="font-manrope text-sm text-muted-foreground leading-relaxed">
                  {fordel.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tjenester */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground" data-testid="gbp-tjenester">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Hva vi gjør
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight">
              Vi hjelper deg hele veien
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {tjenester.map((tjeneste, index) => (
              <div
                key={index}
                className="p-8 border border-primary-foreground/10 rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg flex items-start gap-6 group"
                data-testid={`gbp-tjeneste-${index}`}
              >
                <span className="font-syne font-bold text-4xl text-accent">0{index + 1}</span>
                <div>
                  <h3 className="font-syne font-bold text-xl mb-2">
                    {tjeneste.title}
                  </h3>
                  <p className="font-manrope text-sm text-primary-foreground/70">
                    {tjeneste.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-muted" data-testid="gbp-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-6">
            Klar for lokal synlighet?
          </h2>
          <p className="font-manrope text-muted-foreground max-w-xl mx-auto mb-10">
            Ta kontakt for å lære mer om hvordan vi kan optimalisere din Google Bedriftsprofil.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group"
            data-testid="gbp-bottom-cta"
          >
            Kontakt oss
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
}
